import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiBadge, EuiFieldNumber, EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiInMemoryTable, EuiSearchBar, EuiSelect, EuiSpacer, EuiSwitch, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'
import { EuiSearchBarOnChangeArgs } from '@elastic/eui/src/components/search_bar/search_bar'

import { Account, Campaign, CampaignStatus } from '../../api/interfaces'
import { ReserveSettings } from '../../api/interfaces/ReserveSettings'
import { useGetAccountsQuery } from '../../api/rtkQueryApi/platform/accountsApi'
import { useGetCampaignsQuery, useSetCampaignAgencyReserveSettingsMutation } from '../../api/rtkQueryApi/platform/campaignsApi'
import { RootState } from '../../app/rootReducer'
import { DEFAULT_INITIAL_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../utils/DefaultPageSize'
import { CampaignStatusToElasticColor, CampaignStatusToString } from '../../utils/EnumToFriendly'
import useDebounce from '../../utils/useDebounce'

export const CampaignsAgencySettings: React.FC = () => {
  const { currentOrganization, currentAccount } = useSelector((state: RootState) => state.app)
  const [showEndedCampaigns, setShowEndedCampaigns] = React.useState(false)
  const [queryText, setQueryText] = React.useState('')

  const [displayedCampaigns, setDisplayedCampaigns] = React.useState<Campaign[]>([])
  const [clients, setClients] = React.useState<Account[]>([])
  const [selectedClient, setSelectedClient] = React.useState<Account>()
  const getAllCampaignsQuery = useGetCampaignsQuery({ accountId: selectedClient?.id ?? '', isAgency: true }, { skip: !selectedClient?.id })
  const getAllClientsQuery = useGetAccountsQuery({ isAgency: true })
  const [setCampaignAgencyReserveSettings] = useSetCampaignAgencyReserveSettingsMutation()

  const [editedCampaigns, setEditedCampiagns] = useState<Campaign[]>([])
  const debouncedEditedCampaigns = useDebounce(editedCampaigns, 2000)
  const [isUpdatingCampaigns, setIsUpdatingCampaigns] = useState(false)
  const [currentPage, setCurrentPage] = useState<number>(0)

  useEffect(() => {
    if (currentAccount && clients.length > 0) {
      const currentClient = clients.find(client => client.id === currentAccount.id)
      setSelectedClient(currentClient)
    }
  }, [currentAccount, clients])

  useEffect(() => {
    if (getAllCampaignsQuery.data && !isUpdatingCampaigns && !getAllCampaignsQuery.isFetching) {
      const campaigns = getAllCampaignsQuery.data.filter(campaign => {
        if (!showEndedCampaigns && campaign.status === CampaignStatus.CampaignEnded) return false
        if (queryText) {
          const query = queryText.toLowerCase()
          return campaign.name.toLowerCase().includes(query) || campaign.id.toLowerCase().includes(query)
        }
        return true
      })
      setDisplayedCampaigns(campaigns)
    }
  }, [getAllCampaignsQuery, showEndedCampaigns, queryText, isUpdatingCampaigns])

  useEffect(() => {
    if (getAllClientsQuery.data) {
      setClients(getAllClientsQuery.data)
    }
  }, [getAllClientsQuery.data])

  useEffect(() => {
    if (debouncedEditedCampaigns.length === 0 || !selectedClient?.id) {
      return
    }
    setIsUpdatingCampaigns(true)
    const tasks = debouncedEditedCampaigns.map((campaign: Campaign) => {
      return setCampaignAgencyReserveSettings({
        accountId: selectedClient.id,
        campaignId: campaign.id,
        agencyReserveSettings: campaign.campaignReserveSettings ?? { inheritAgencyReserveRate: true, reserveRate: 0 }
      }).unwrap()
    })
    Promise.all(tasks).finally(() => {
      setEditedCampiagns([])
      setIsUpdatingCampaigns(false)
    })
  }, [debouncedEditedCampaigns, selectedClient])

  const onAgencyReserveSettingsChange = (campaignId: string, settings: ReserveSettings) => {
    const editedCampaign = displayedCampaigns.find(c => c.id === campaignId)
    setDisplayedCampaigns(prevCampaigns => prevCampaigns.map(campaign => (campaign.id === campaignId ? { ...campaign, campaignReserveSettings: settings } : campaign)))
    setEditedCampiagns(prevCampaigns => {
      let excludePrevClients = prevCampaigns.filter(c => c.id !== campaignId)
      excludePrevClients.push({ ...editedCampaign, campaignReserveSettings: settings } as Campaign)
      return excludePrevClients
    })
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const campaignsAgencyReserveSettingsColumns: Array<EuiBasicTableColumn<Campaign>> = [
    {
      width: '25%',
      name: 'Campaign Name',
      field: 'name',
      sortable: true
    },
    {
      name: 'Client',
      render: (campaign: Campaign) => {
        const client = clients.find(c => c.id === campaign.accountId)
        return client ? client.name : ''
      },
      sortable: (campaign: Campaign) => {
        const client = clients.find(c => c.id === campaign.accountId)
        return client ? client.name : ''
      }
    },
    {
      name: `Type`,
      render: (campaign: Campaign) => {
        return <div className='campaignType'>{campaign.type}</div>
      },
      sortable: (campaign: Campaign) => campaign.type
    },
    {
      name: `Budget`,
      render: (campaign: Campaign) => {
        return formatter.format(campaign.budget) + ' ' + campaign.budgetFrequency.toLowerCase()
      },
      sortable: (campaign: Campaign) => campaign.budget
    },
    {
      name: `Status`,
      render: (c: Campaign) => <EuiBadge color={CampaignStatusToElasticColor(c.status)}>{CampaignStatusToString(c)}</EuiBadge>,
      sortable: (c: Campaign) => c.status
    },
    {
      name: 'Use Custom Reserve Rate',
      render: (campaign: Campaign) => (
        <EuiSwitch
          id={`inheritAgencyReserveSwitch-${campaign.id}`}
          checked={!(campaign.campaignReserveSettings?.inheritAgencyReserveRate ?? true)}
          onChange={() => {
            const newSettings = {
              inheritAgencyReserveRate: !(campaign.campaignReserveSettings?.inheritAgencyReserveRate ?? true),
              reserveRate: campaign.campaignReserveSettings?.reserveRate ?? 0
            }
            onAgencyReserveSettingsChange(campaign.id, newSettings)
          }}
          label={''}
        />
      ),
      sortable: (campaign: Campaign) => campaign.campaignReserveSettings?.inheritAgencyReserveRate ?? true
    },
    {
      name: 'Reserve Rate',
      render: (campaign: Campaign) =>
        campaign.campaignReserveSettings?.inheritAgencyReserveRate ?? true ? (
          selectedClient?.accountReserveSettings?.inheritAgencyReserveRate ?? true ? (
            `${((currentOrganization?.organizationReserveSettings?.reserveRate ?? 0) * 100).toFixed()}%`
          ) : (
            `${((selectedClient?.accountReserveSettings?.reserveRate ?? 0) * 100).toFixed()}%`
          )
        ) : (
          <EuiFieldNumber
            type='number'
            value={((campaign.campaignReserveSettings?.reserveRate ?? 0) * 100).toFixed()}
            onChange={e => {
              const newRate = Math.min(Math.max(Number(e.target.value) / 100, 0), 0.2)
              const newSettings = {
                inheritAgencyReserveRate: false,
                reserveRate: newRate
              }
              onAgencyReserveSettingsChange(campaign.id, newSettings)
            }}
            append={'%'}
          />
        ),
      sortable: (campaign: Campaign) => campaign.campaignReserveSettings?.reserveRate ?? 0
    }
  ]

  const clientsOptions = clients.map(client => ({
    value: client.id,
    text: client.name
  }))

  const searchOnChange = ({ queryText, error }: EuiSearchBarOnChangeArgs) => {
    if (error) {
      return
    } else {
      setQueryText(queryText)
    }
  }

  const sorting = {
    sort: {
      field: 'name',
      direction: SortDirection.ASC
    }
  }

  const pagination = {
    initialPageSize: DEFAULT_INITIAL_PAGE_SIZE,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    pageIndex: currentPage
  }

  return (
    <>
      <EuiFormRow label={'Client'}>
        <EuiSelect
          name='clientsSelect'
          options={clientsOptions}
          value={selectedClient?.id}
          onChange={e => {
            const clientId = e.target.value
            const client = clients.find(c => c.id === clientId)
            setSelectedClient(client)
          }}
        />
      </EuiFormRow>
      <EuiSpacer />
      <EuiSearchBar
        toolsRight={
          <EuiFlexGroup
            justifyContent={'center'}
            alignItems={'center'}
            style={{
              paddingRight: '12px'
            }}>
            <EuiFlexItem grow={false}>
              <EuiSwitch onChange={() => setShowEndedCampaigns(!showEndedCampaigns)} label={'Show Ended'} checked={showEndedCampaigns} />
            </EuiFlexItem>
          </EuiFlexGroup>
        }
        box={{ incremental: true }}
        onChange={searchOnChange}
      />
      <EuiSpacer />
      <EuiInMemoryTable items={displayedCampaigns} columns={campaignsAgencyReserveSettingsColumns} loading={getAllCampaignsQuery.isLoading || !selectedClient || !currentOrganization} sorting={sorting} pagination={pagination} onTableChange={({ page: { index } }) => setCurrentPage(index)} responsive={false} />
    </>
  )
}
